import React from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Box, Paragraph, Text } from 'theme-ui';
import { DonutCardProps } from '../../../../../types/dashboard.types';
import DonutCardSkeleton from '../../skeleton/DonutCardSkeleton';
import { useSelector } from 'react-redux';

// allow parent to control the rendered chart size so all cards can be uniform
type Props = DonutCardProps & { size?: number };

const DonutCard: React.FC<Props> = ({ id, title, totalDone, segments, size = 300, loading, colorMode }) => {
    const { flag } = useSelector((state: any) => state.dashboardData || {});
    if ((flag === "all-data" ||
        flag === "dashboard-data" ||
        flag === "dashboard-time-data" ||
        flag === "dashboard-project-type-data" ||
        flag !== "dashboard-trades-type-worker-manager") &&
        (loading || !title)) {
        return <DonutCardSkeleton />
    }

    const segTotal = typeof totalDone === 'number' && totalDone > 0 ? totalDone : (segments || []).reduce((s, x) => s + (x.value || 0), 0);

    const seriesData = (segments || []).map((s) => ({ value: s.value, name: s.label, itemStyle: { color: s.color } }));

    const option: echarts.EChartsOption = {
        tooltip: {
            trigger: 'item',
            confine: true,
            extraCssText: 'border-radius:8px; box-shadow:0 6px 18px rgba(0,0,0,0.08); padding:6px 10px; background:#fff;',
            formatter: function (params: any) {
                const p = Array.isArray(params) ? params[0] : params;
                const color = p.color || (p.data && p.data.itemStyle && p.data.itemStyle.color) || '#ccc';
                const name = p.name || '';
                const value = p.value || 0;
                const percent = Math.round(p.percent || 0);
                return (`<div class="donut-tooltip"><span class="dot" style="background:${color}"></span><div class="text">${name}: <span class="value">${value}</span> <span class="percent">(${percent}%)</span></div></div>`);
            }
        },
        legend: { show: false },
        series: [
            {
                name: title ?? undefined,
                type: 'pie',
                radius: ['60%', '82%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: false,
                label: { show: false },
                emphasis: { label: { show: false } },
                labelLine: { show: false },
                data: seriesData
            }
        ]
    };



    return (
        <div style={{ width: "100%" }}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>

                <div style={{ width: "100%", height: "100%", maxHeight: size, maxWidth: '100%', position: 'relative' }}>
                    <ReactECharts echarts={echarts} option={option} style={{ height: size, width: "100%" }} />

                    <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', textAlign: 'center', width: '70%' }}>
                        <Text sx={{ color: '#9a8f8f', fontWeight: 400, fontSize: [10, 11, 12], lineHeight: 1 }}>
                            Total Done
                        </Text>
                        <Paragraph sx={{ color: colorMode === 'light' ? '#3b2f2f' : '#f8f9fa', fontWeight: 700, fontSize: [14, 16, 20, 24, 28], lineHeight: 1 }}>
                            {totalDone}
                        </Paragraph>
                    </Box>
                </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {(segments || []).map((seg) => {
                        const percent = segTotal > 0 ? Math.round((seg.value / segTotal) * 100) : 0;
                        return (
                            <Box key={seg.key} sx={{ display: 'flex', alignItems: 'center', gap: "8px", background: '#F7EFE5', padding: '4px 8px', borderRadius: '6px' }}>
                                <Box sx={{ variant: 'badges.legendDot', bg: seg.color || '#ccc' }} />
                                <Text sx={{ fontSize: ["12px", "9px", "10px", "12px"], color: 'rgba(69, 53, 33, 1)', fontWeight: 500 }}>
                                    {seg.label}: <Text as="span" >{seg.value}</Text> <Text as="span" >({percent}%)</Text>
                                </Text>
                            </Box>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default React.memo(DonutCard);
